import { monthlyPI } from "@zillow/mortgage-calculations";
import { useFormContext } from "react-hook-form";
import { useLastValidValues } from "../../utils/form/useLastValidValues.esm.js";
import { LOAN_PROGRAMS, COMMON_PERCENT_CONFIG, COMMON_AMOUNT_CONFIG, LOAN_TERM_IN_MONTHS, commaSeparateNumber } from "../../utils/zhl-calculator.esm.js";
const options = [{
  value: LOAN_PROGRAMS.fixed30Year,
  label: "30-year fixed"
}, {
  value: LOAN_PROGRAMS.fixed20Year,
  label: "20-year fixed"
}, {
  value: LOAN_PROGRAMS.fixed15Year,
  label: "15-year fixed"
}, {
  value: LOAN_PROGRAMS.fixed10Year,
  label: "10-year fixed"
}];
const cashOutputs = {
  adjustedEstimate: "Adjusted estimate",
  combinedLoanToValue: "Combined loan-to-value",
  newLoanBalance: "New loan balance",
  newMonthlyPayment: "New monthly payment"
};
const OUTPUTS = [cashOutputs.adjustedEstimate, cashOutputs.combinedLoanToValue, cashOutputs.newLoanBalance, cashOutputs.newMonthlyPayment];
const formatAmount = (amount) => {
  return "$" + commaSeparateNumber(amount);
};
const getResult = (label, values) => {
  const {
    calculations
  } = values;
  if (label === cashOutputs.adjustedEstimate) {
    return formatAmount(calculations.adjustedCashOutEstimate);
  } else if (label === cashOutputs.combinedLoanToValue) {
    return Number(calculations.combinedLoanToValue.toFixed(2)) + "%";
  } else if (label === cashOutputs.newLoanBalance) {
    return formatAmount(calculations.newLoanBalance);
  } else {
    return formatAmount(Math.round(calculations.monthlyPI));
  }
};
const FieldConfig = {
  homeValue: {
    name: "homeValue",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  currentMortgageBalance: {
    name: "currentMortgageBalance",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  newLoanTerm: {
    name: "newLoanTerm",
    options,
    config: {
      required: "Required",
      validate: {
        invalidProgram: (value) => {
          let error = null;
          if (Object.values(LOAN_PROGRAMS).includes(value) === false) {
            error = "Invalid loan program";
          }
          return error;
        }
      }
    }
  },
  newInterestRate: {
    name: "newInterestRate",
    config: {
      ...COMMON_PERCENT_CONFIG
    }
  },
  adjustCashOutAmount: {
    name: "adjustCashOutAmount",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  fees: {
    name: "fees",
    config: {
      ...COMMON_AMOUNT_CONFIG
    }
  },
  rollFees: {
    name: "rollFees"
  }
};
function useLastValidCashOutRefinanceValues() {
  const form = useFormContext();
  return useLastValidValues(form, getCashOutRefinanceValues);
}
function getCashOutRefinanceValues(lastValidFields) {
  const {
    homeValue,
    currentMortgageBalance,
    newInterestRate,
    newLoanTerm,
    adjustCashOutAmount,
    rollFees,
    fees
  } = lastValidFields;
  const includeFees = rollFees ? fees : 0;
  const EstimatedMaxCashOutAmount = homeValue * 80 / 100 - currentMortgageBalance;
  const newLoanBalance = currentMortgageBalance + adjustCashOutAmount + includeFees;
  const combinedLoanToValue = newLoanBalance / homeValue * 100;
  const adjustedCashOutEstimate = homeValue * (combinedLoanToValue / 100) - (currentMortgageBalance + includeFees);
  const calculations = {
    EstimatedMaxCashOutAmount,
    combinedLoanToValue,
    newLoanBalance,
    adjustedCashOutEstimate,
    monthlyPI: monthlyPI(newLoanBalance, newInterestRate, LOAN_TERM_IN_MONTHS[newLoanTerm])
  };
  const values = {
    fields: lastValidFields,
    calculations
  };
  return values;
}
export {
  FieldConfig,
  OUTPUTS,
  cashOutputs,
  getResult,
  useLastValidCashOutRefinanceValues
};
